// Procedure module — same Document API/UI as Documents, scoped to procedure types

import { createTrackingConfig } from '../../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../../config/appConfig';
import CustomDocumentSplitView from '../../document-management/document/CustomDocumentSplitView';

export const procedureConfig = {
  entityName: 'Procedure',

  listScope: {
    documentTypeFlag: 'isProcedure',
  },
  
  // Entity information for file attachments
  entityTypeId: 1, // Documents entity type ID

    // UI Configuration - Using centralized app config
    ui: {
      drawerWidth: '40%', // 30% of table width 
      defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
      pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
      defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
      requiredFieldStyle: uiConfig.requiredFieldStyle, // From app config: 'badge'
      tableVariant: 'default',// 'default' | 'modern' | 'cards' | 'board' | 'timeline' 
                              // default: default table with cards
                              // cards: cards table
                              // board: board table
                              // timeline: timeline table
      splitView: true,
      searchInputMaxWidth: uiConfig.searchInputMaxWidth, // From app config: '60%'
    },
  
  customSplitViewPanel: {
    enabled: true,
    component: CustomDocumentSplitView,
  },

  // Features configuration
  features: {
    fileAttachments: false,
    masterEntityTypeId: 1,
    approvalRequests: true,
    procedureAssignments: true,
    // Revision uploads on Procedures are PDF-only
    revisionFileAccept: '.pdf,application/pdf',
  },

  // Review and Approval configuration
  approval: {
    enabled: true, // Master switch for review and approval functionality
    showInForm: true, // Show review and approval request button in document form
    showInDetails: false, // Show review and approval integration in document details tab
    showInRevisions: true, // Show review and approval integration in document revisions tab
    requireApprovalForPublish: false, // Require review and approval before publishing document
    allowMultipleApprovals: true, // Allow multiple review and approval requests per document
    defaultApprovalType: 'single', // Default approval type when creating requests
    approvalButtonText: 'Request Review and Approval', // Text for review and approval request button
    approvalButtonIcon: 'CheckCircleIcon', // Icon component name (from Icons.js) for approval button icon
    showApprovalStatus: true, // Show current review and approval status in document view
    showApprovalActions: true, // Show approval action buttons (approve, reject, etc.)
    showApprovalHistory: true, // Show review and approval history in document timeline
    formMode: 'modal', // Use modal popup for review and approval form
  },
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/document-management/document',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      deprecate: (id) => `/${id}/deprecate`,
      publish: (id) => `/${id}/publish`,
      archive: (id) => `/${id}/archive`,
      unarchive: (id) => `/${id}/unarchive`,
      setCurrentRevision: (id) => `/${id}/set-current-revision`,
      timeline: (id) => `/${id}/timeline`,
      byStatus: (statusId) => `/status/${statusId}`,
      byDepartment: (departmentId) => `/department/${departmentId}`,
      published: '/published/all',
      archived: '/archived/all',
      deprecated: '/deprecated/all',
      searchByCode: '/search/by-code',
      searchByTitle: '/search/by-title',
      // File attachment endpoints
      fileAttachments: (id) => `/${id}/file-attachments`,
      fileAttachmentCount: (id) => `/${id}/file-attachment-count`,
      withFileAttachments: (id) => `/${id}/with-file-attachments`,
      // Revision endpoints
      revisions: (id) => `/${id}/revisions`,
      revisionCount: (id) => `/${id}/revision-count`
    },
    lookupEndpoints: {
      documentTypes: '/document-type',
      documentStatuses: '/document-status',
      departments: '/department',
      users: '/users',
      approvalTemplates: '/approval-templates',
      documentModules: '/document-module'
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/document-management/document',
      withFileAttachments: '/document-management/document/with-file-attachments',
      fileAttachments: (entityTypeId) => `/file-attachment/entity/${entityTypeId}/*`
    }
  },
  
 
  // Service configuration
  service: {
    name: 'procedureService',
    methods: {
      getAll: 'getAllDocuments',
      getById: 'getDocumentById',
      create: 'createDocument',
      update: 'updateDocument',
      delete: 'deleteDocument',
      validate: 'validateData',
    },
  },
  // UNIFIED FIELD CONFIGURATION - Single source of truth
  
  // The GenericModulePage automatically:
  // 1. Takes 'documentTypeId' field name
  // 2. Removes 'Id' suffix → 'documentType'  
  // 3. Finds entity.documentType nested object
  // 4. Extracts entity.documentType.name 
  // 5. Sets enhanced.documentType = "Policy Documents"
  // 6. Table displays this in the 'Type' column
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    // Code
    { 
      name: 'code', 
      label: 'Code', 
      type: 'text', 
      required: true,
      placeholder: 'Enter document code',
      table: { header: 'Code', width: '120px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        minLength: 3,
        maxLength: 255
      }
    },
    // Title
    { 
      name: 'title', 
      label: 'Procedure Title',
      type: 'text',
      required: true,
      placeholder: 'Enter procedure title',
      table: { header: 'Title', width: '300px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        minLength: 3,
        maxLength: 255
      }
    },
    // Current Revision
    { 
      name: 'currentRevision', 
      label: 'Current Revision', 
      type: 'label', // Use label type for read-only display
      table: { 
        header: 'Current Revision', 
        width: '120px', 
        sortable: true,
        displayField: 'currentRevision',
        cellRenderer: 'badge'
      },
      filterable: false
    },
    // Classification & Assignment
    { 
      name: 'documentTypeId', 
      label: 'Doc.Type', 
      type: 'select', 
      required: true,
      lookupEndpoint: 'documentTypes',
      lookupFilter: (type) => type.isProcedure === true,
      options: [],
      table: {
        header: 'Type',
        width: '150px',
        sortable: true,
        displayField: 'documentType',
        cellRenderer: 'badge',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
      }
    },
    // Department (multiselect)
    { 
      name: 'departmentIds', 
      label: 'Department', 
      type: 'multiSelect', 
      required: true,
      lookupEndpoint: 'departments',
      labelField: 'name',
      valueField: 'id',
      options: [],
      table: { 
        header: 'Department', 
        width: '200px', 
        sortable: false,
        displayField: 'documentDepartments',
        cellRenderer: 'array'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        array: true,
        message: 'Please select at least one department'
      }
    },
    // Status
    { 
      name: 'statusId', 
      label: 'Status', 
      type: 'select', 
      required: true,
      lookupEndpoint: 'documentStatuses',
      options: [],
      table: { 
        header: 'Status', 
        width: '120px', 
        sortable: true,
        displayField: 'status',
        cellRenderer: 'statusBadge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
      }
    },
    // Approval Template
    { 
      name: 'approvalTemplateId', 
      label: 'Approval Template', 
      type: 'select', 
      required: false,
      lookupEndpoint: 'approvalTemplates',
      options: [],
      table: { 
        header: 'Approval Template', 
        width: '180px', 
        sortable: true,
        displayField: 'approvalTemplate',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: false,
      }
    },
    // Module — locked to Procedures for this module
    { 
      name: 'moduleId', 
      label: 'Module', 
      type: 'select', 
      required: true,
      disabled: true,
      showLockIcon: true,
      lockedLookupName: 'PROCEDURES',
      lookupEndpoint: 'documentModules',
      lookupFilter: (module) =>
        String(module?.name || '').toUpperCase() === 'PROCEDURES',
      labelField: 'description', // Use description instead of name for dropdown
      options: [],
      table: { 
        header: 'Module', 
        width: '180px', 
        sortable: true,
        displayField: 'module',
        cellRenderer: 'badge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
      }
    },
    // Notes
    { 
      name: 'notes', 
      label: 'Notes', 
      type: 'textarea',
      placeholder: 'Enter additional notes',
      rows: 4,
      table: { header: 'Notes', width: '300px', sortable: true },
      filterable: false
    },    
    // Published Information
    { 
      name: 'publishedAt', 
      label: 'Published Date', 
      type: 'date',
      table: { 
        header: 'Published', 
        width: tableConfig.dateColumnWidth, // From app config: '120px'
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
      },
      filterable: true,
      filterType: 'dateRange'
    },
    { 
      name: 'publishedById', 
      label: 'Published By ID', 
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'publishedReason', 
      label: 'Published Reason', 
      type: 'textarea',
      placeholder: 'Enter reason for publication',
      table: { header: 'Published Reason', width: '260px', sortable: true },
      filterable: false
    },
    
    // Deprecation Information
    { 
      name: 'deprecated', 
      label: 'Deprecated Status', 
      type: 'switch', 
      switchLabel: 'Deprecated', 
      description: 'Mark this document as deprecated', 
      labelPosition: 'left',
      table: { 
        header: 'Deprecated', 
        width: '100px',
        sortable: true,
        cellRenderer: 'booleanBadge'
      },
      filterable: true,
      filterType: 'boolean'
    },
    { 
      name: 'deprecatedAt', 
      label: 'Deprecated Date', 
      type: 'date',
      table: { 
        header: 'Deprecated', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
    },
    { 
      name: 'deprecatedById', 
      label: 'Deprecated By ID', 
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'deprecatedReason', 
      label: 'Deprecated Reason', 
      type: 'textarea',
      placeholder: 'Enter reason for deprecation',
      table: { header: 'Deprecated Reason', width: '260px', sortable: true },
      filterable: false
    },
    
    // Archive Information
    { 
      name: 'archivedAt', 
      label: 'Archived Date', 
      type: 'date',
      table: { 
        header: 'Archived', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
    },
    { 
      name: 'archivedById', 
      label: 'Archived By ID', 
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'archiveReason', 
      label: 'Archive Reason', 
      type: 'textarea',
      placeholder: 'Enter reason for archiving',
      table: { header: 'Archive Reason', width: '260px', sortable: true },
      filterable: false
    },
    
    // Revision Information
    // { 
    //   name: 'currentRevisionId', 
    //   label: 'Current Revision ID', 
    //   type: 'hidden',
    //   table: false,
    //   filterable: false
    // },
    // Tracking Information (display only)
    // { 
    //   name: 'createdAt', 
    //   label: 'Created Date', 
    //   type: 'date', 
    //   readonly: true,
    //   table: { 
    //     header: 'Created', 
    //     width: tableConfig.dateColumnWidth, // From app config: '120px'
    //     sortable: true,
    //     cellRenderer: 'date',
    //     dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
    //   },
    //   filterable: true,
    //   filterType: 'dateRange'
    // },
    // { 
    //   name: 'createdBy', 
    //   label: 'Created By', 
    //   type: 'text', 
    //   readonly: true,
    //   table: { header: 'Created By', width: '180px', sortable: true, displayField: 'createdBy' },
    //   filterable: false
    // },
    // { 
    //   name: 'createdById', 
    //   label: 'Created By ID', 
    //   type: 'hidden',
    //   table: false,
    //   filterable: false
    // },
    // { 
    //   name: 'updatedAt', 
    //   label: 'Last Updated', 
    //   type: 'date', 
    //   readonly: true,
    //   table: { 
    //     header: 'Updated', 
    //     width: tableConfig.dateColumnWidth, // From app config: '120px'
    //     sortable: true,
    //     cellRenderer: 'date',
    //     dateFormat: getDateFormat('table') // From app config: 'dd/MM/yyyy'
    //   },
    //   filterable: true,
    //   filterType: 'dateRange'
    // },
    // { 
    //   name: 'updatedBy', 
    //   label: 'Updated By', 
    //   type: 'text', 
    //   readonly: true,
    //   table: { header: 'Updated By', width: '180px', sortable: true, displayField: 'updatedBy' },
    //   filterable: false
    // },
    // { 
    //   name: 'updatedById', 
    //   label: 'Updated By ID', 
    //   type: 'hidden',
    //   table: false,
    //   filterable: false
    // }
  ],
  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,  // Use displayField as key if available
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name  // Keep reference to original field name
      }));
    
    return columns;
  },

  exportFileName: 'qms_procedures',
  
  // Custom handlers for document-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in documentPage
    onDelete: null, // Will be defined in documentPage
    onLoad: null, // Backend now provides fileAttachmentCount directly
    onFormOpen: null, // Will be defined in documentPage
    
    // File upload handlers
    onFileUpload: null, // Will handle file upload after document save
    onFileRemove: null // Will handle file removal
  },
  
  // Data transformation before submit to ensure correct types for backend/Prisma
  transformData: (data) => {
    const transformed = { ...data };

    // Remove fileAttachments from the main document data (handled separately)
    delete transformed.fileAttachments;

    // Audit fields are server-managed (user-id header + Prisma @default/@updatedAt)
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;

    // Numeric foreign keys — optional FKs send null when cleared (do not omit from PATCH)
    const nullableIntFields = ['approvalTemplateId', 'moduleId'];
    const intFields = [
      'documentTypeId', 'statusId', 'approvalTemplateId', 'moduleId',
      'currentRevisionId',
      'publishedById', 'deprecatedById', 'archivedById'
    ];
    intFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          if (nullableIntFields.includes(field)) {
            transformed[field] = null;
          } else {
            delete transformed[field];
          }
        } else {
          const n = parseInt(transformed[field], 10);
          if (!Number.isNaN(n)) transformed[field] = n; else delete transformed[field];
        }
      }
    });

    // Multi-department IDs
    if (Array.isArray(transformed.departmentIds)) {
      transformed.departmentIds = transformed.departmentIds
        .map((id) => parseInt(id, 10))
        .filter((id) => Number.isFinite(id));
    } else if (transformed.departmentIds === '' || transformed.departmentIds == null) {
      transformed.departmentIds = [];
    }
    delete transformed.departmentId;
    delete transformed.department;
    delete transformed.departments;
    delete transformed.documentDepartments;

    // Dates
    const dateFields = ['publishedAt', 'deprecatedAt', 'archivedAt'];
    dateFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const d = new Date(transformed[field]);
          if (!Number.isNaN(d.getTime())) transformed[field] = d; else delete transformed[field];
        }
      }
    });

    // Booleans
    if (transformed.deprecated !== undefined) {
      if (typeof transformed.deprecated === 'string') {
        transformed.deprecated = transformed.deprecated === 'true';
      } else {
        transformed.deprecated = Boolean(transformed.deprecated);
      }
    }

    return transformed;
  },
  
  // Backend field mappings
  fieldMappings: {
    // Map frontend display fields to backend field paths
    documentType: 'documentType.name',
    department: 'department.name',
    documentDepartments: 'documentDepartments',
    status: 'status.name',
    approvalTemplate: 'approvalTemplate.name',
    module: 'module.name',
    createdBy: 'createdBy.userDisplayName',
    updatedBy: 'updatedBy.userDisplayName',
    publishedBy: 'publishedBy.userDisplayName',
    deprecatedBy: 'deprecatedBy.userDisplayName',
    archivedBy: 'archivedBy.userDisplayName',
    currentRevision: 'currentRevision.version'
  },
  
  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['title', 'code', 'departmentIds', 'statusId', 'approvalTemplateId', 'moduleId'],
    },
    {
      name: 'advanced',
      label: 'Advanced',
      icon: '⚙️',
      fields: ['publishedAt', 'deprecated', 'deprecatedAt', 'archivedAt'],
    },
  ],

  // Custom Sort Orders - Define logical sorting for non-database categorical fields
  customSortOrders: {
    // Boolean values
    deprecated: [false, true], // Active documents first, then deprecated
    
    // Note: Database model fields (status, documentType, department) will use 
    // their lookup data with 'sortOrder' or 'displayOrder' field from the database
    // This ensures we use real data instead of hardcoded values
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },
  
  // Notification messages
  messages: {
    loading: 'Loading procedures...',
    noData: 'No procedures found. Create your first procedure to get started.',
    createSuccess: 'Procedure created successfully!',
    updateSuccess: 'Procedure updated successfully!',
    deleteSuccess: 'Procedure deleted successfully!',
    createError: 'Failed to create procedure',
    updateError: 'Failed to update procedure',
    deleteError: 'Failed to delete procedure',
    loadError: 'Failed to load procedures',
    validationError: 'Validation failed',
  },
  
  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Basic Information',
      icon: '📄',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['code', 'title', 'notes'],
      fieldSpans: {
        'code': 'md:col-span-4',
        'title': 'md:col-span-8', 
        'notes': 'md:col-span-12'
      }
    },
    {
      title: 'Classification & Assignment',
      icon: '🏷️',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['documentTypeId', 'departmentIds', 'statusId', 'approvalTemplateId'],
      fieldSpans: {
        'documentTypeId': 'md:col-span-6',
        'departmentIds': 'md:col-span-6',
        'statusId': 'md:col-span-6',
        'approvalTemplateId': 'md:col-span-6'
      }
    },
    {
      title: 'Published Information',
      icon: '📋', 
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['publishedAt', 'publishedReason'],
      fieldSpans: {
        'publishedAt': 'md:col-span-4',
        'publishedReason': 'md:col-span-8'
      }
    },
    {
      title: 'Deprecation Information',
      icon: '⚠️',
      layout: 'mixed', // Special layout for switch + grid
      fields: ['deprecated', 'deprecatedAt', 'deprecatedReason']
    },
    {
      title: 'Archive Information',
      icon: '📦',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['archivedAt', 'archiveReason'],
      fieldSpans: {
        'archivedAt': 'md:col-span-4',
        'archiveReason': 'md:col-span-8'
      }
    },
    {
      title: 'Revision Information',
      icon: '🔄',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['currentRevision'],
      fieldSpans: {
        'currentRevision': 'md:col-span-4'
      }
    },
    {
      title: 'Assigned to Module',
      icon: '📦',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['moduleId'],
      fieldSpans: {
        'moduleId': 'md:col-span-6'
      }
    }
  ],

  // Tracking Section Configuration (automatically generated from centralized labels)
  trackingSection: createTrackingConfig('Procedure'),

  // Labels and Text Configuration (uses predefined document labels + custom overrides)
  labels: {
    // Using predefined document labels from centralized labels file
    // You can override any label by uncommenting and modifying:
  },

  // Centralized field help text (MVP: short tooltips)
  helpText: {
    code: { short: 'Unique code used in references and audits.' },
    title: { short: 'Human-readable document name used in lists and search.' },
    documentTypeId: { short: 'Document type/category; drives workflows and retention.' },
    departmentIds: { short: 'Owning department(s); used for scope and reporting.' },
    statusId: { short: 'Workflow state (Draft/Review/Approved/Obsolete).' },
    approvalTemplateId: { short: 'Approval template to use for this document workflow.' },
    moduleId: { short: 'Locked to Procedures for this page.' },
    publishedAt: { short: 'Date when this version becomes effective.' },
    publishedReason: { short: 'Reason or context for publication.' },
    deprecated: { short: 'Marks the document as deprecated (no longer active).' },
    deprecatedAt: { short: 'Date deprecation became effective.' },
    deprecatedReason: { short: 'Reason for deprecation and replacement guidance.' },
    archivedAt: { short: 'Date moved to archive; retained but not active.' },
    archiveReason: { short: 'Reason for archiving the document.' },
    notes: { short: 'Additional context or remarks for reviewers/users.' }
  },

  // Fields to remove during data preparation for submission (used by BaseService)
  fieldsToRemove: [
    'documentType', 'department', 'departments', 'documentDepartments', 'status', 'approvalTemplate', 'module', 'createdBy', 'updatedBy',
    'createdById', 'updatedById', 'createdAt', 'updatedAt',
    'publishedBy', 'deprecatedBy', 'archivedBy',
    'currentRevision', 'steps', 'fileAttachments', 'fileAttachmentCount'
  ],

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null, // Will be set after validation module loads

  // Helper methods for approval functionality
  isApprovalEnabled: function() {
    return this.features.approvalRequests && this.approval.enabled;
  },

  shouldShowApprovalInForm: function() {
    return this.isApprovalEnabled() && this.approval.showInForm;
  },

  shouldShowApprovalInDetails: function() {
    return this.isApprovalEnabled() && this.approval.showInDetails;
  },

  shouldShowApprovalInRevisions: function() {
    return this.isApprovalEnabled() && this.approval.showInRevisions;
  },

  requiresApprovalForPublish: function() {
    return this.isApprovalEnabled() && this.approval.requireApprovalForPublish;
  }
};
